import type { TFunction } from 'i18next'

/** Adlı varyantın anahtar eki — bkz. {@link rowActionLabel}. */
const NAMED_SUFFIX = 'Named'

/**
 * Bir liste satırındaki eylem düğmesinin erişilebilir adını üretir: **satırın adını içerir**.
 *
 * <p><b>Neden gerekli.</b> Aynı listedeki her silme düğmesi aynı sabit adı taşıdığında
 * ("Görevi Sil" / "Delete Task", "Kaldır" / "Remove") ekran okuyucu ve sesli kontrol kullanıcısı
 * düğmeleri birbirinden ayırt edemez: erişilebilir ad, sesli kontrolde düğmenin **tek**
 * tanımlayıcısıdır ("Delete Task'a tıkla" hangi satırı kastediyor?). Görsel kullanıcı satırın
 * hizasından anlar, ekran okuyucu kullanıcısı anlayamaz. THY'nin PR inceleme aracı bunu
 * `ceo-frontend` PR #32'de beş ayrı yerde MEDIUM olarak raporladı (14.09.2026); ölçtüğümüzde kapsam
 * **yedi bileşende on iki çağrı yeriydi** — raporun gördüğünden geniş. (İlk sayım altı/on demişti;
 * alt görev listesi —`ActionDetailPage` masaüstü kolonu ve `SubActionCards`— ikinci turda eklendi.)
 *
 * <p><b>Anahtar sözleşmesi.</b> `baseKey` adsız anahtardır; adlı karşılığı aynı anahtarın
 * `{@value #NAMED_SUFFIX}` ekli hâlidir ve `{{name}}` interpolasyonu içerir. Örnek:
 * `actions.detail.delete.action` ↔ `actions.detail.delete.actionNamed`. Ek uydurma değildir: aynı
 * düğümde zaten `confirmNamed` vardı (silme onayı metni, `{{name}}` ile) — bu yardımcı o kalıbı
 * yaygınlaştırıyor, yenisini getirmiyor. İkisinin birlikte var olması
 * `deleteLabel.test.ts` ile zorlanır — anahtar şablonla kurulduğu için eksik bir çeviri derleme
 * zamanında değil, ancak ekranda (ham anahtar metni olarak) görülürdü.
 *
 * <p><b>Ad boşsa adsız anahtara düşülür.</b> Boş bir `{{name}}` interpolasyonu " görevini sil" gibi
 * baştan boşluklu bir metin üretir; bu ne okunabilir ne de ayırt edicidir, yani düzeltmenin amacını
 * boşa çıkarır. Boş ad gerçek bir durumdur: tutanak madde editöründe (`MinuteItemsEditor`) henüz adı
 * girilmemiş taslak satırın da silme düğmesi vardır. Aksiyon editörlerinde `row.id != null` kapısı
 * zaten yalnız kaydedilmiş satırları çizdiği için orada ad hep doludur; yine de aynı yol kullanılır
 * ki çağrı yerleri arasında davranış farkı olmasın.
 *
 * <p>Aynı değer `title` için de kullanılır: fare kullanıcısının gördüğü ipucu ile ekran okuyucunun
 * okuduğu ad ayrışmamalıdır.
 * Bir liste satırındaki **herhangi bir eylem** düğmesinin erişilebilir adını üretir. Asıl uygulama
 * budur; {@link deleteLabel} silme için bunun adlandırılmış kısayoludur.
 *
 * <p>Genel ad 18.09.2026'da açıldı (THY inceleme bulgusu, `ceo-frontend` PR): kalıp yalnız silmeye
 * özgü değilmiş — **Düzenle** düğmeleri de tekrar eden satırlarda sabit ad taşıyordu ("Düzenle"),
 * yani aynı listede birbirinden ayırt edilemiyordu. Aynı satırdaki silme ve dosya düğmeleri satır
 * adını taşırken düzenlemenin taşımaması ayrıca tutarsızdı.
 *
 * <p>`deleteLabel` **yeniden adlandırılMADI**: 39 çağrı yeri var ve salt-ad değişikliği teslim
 * diff'ini gereksiz büyütürdü. İkisi de aynı gövdeyi çağırdığı için davranış tek yerde kalır.
 *
 * <p><b>⚠️ KUSUR SINIFI HENÜZ TÜMÜYLE KAPANMADI — ölçülen kalan kapsam: 55 çağrı yeri / 36 dosya</b>
 * (18.09.2026). Tarama:
 *
 * <pre>
 * grep -rnE "aria-label=\{t\('[^']*\.(edit|delete|remove|view|detail|toggle)'\)\}" frontend/src --include=*.tsx | grep -v /test/
 * </pre>
 *
 * Bunların çoğu admin/lookup ekranlarının `*Columns.tsx` dosyalarıdır ve <b>silme</b> düğmeleri de
 * dâhildir — yani ilk turun taraması o ekranları hiç kapsamamış. O turda düzeltilenler yalnız THY
 * incelemesinin işaret ettiği yüzeylerdi.
 *
 * <p><b>Neden aynı turda bitirilmedi:</b> 34 dosya + iki dilde ~40 anahtar çifti + etiketi birebir
 * sorgulayan testlerin güncellenmesi demek (üç düzeltme bile yedi testi kırdı). Hepsi THY'nin
 * `develop`'ında zaten duruyor ve teslim PR'ının diff'inde <b>değil</b>, yani incelemeye takılmıyor;
 * teslim paketine karıştırmak hem karşı tarafın diff'ini okunmaz kılar hem gereksiz risk taşır.
 * Kendi turunda, testleriyle birlikte yapılacak (kullanıcı kararı 18.09.2026).
 *
 * <p>`params`: adın yanında etiketin taşıdığı ek interpolasyonlar (ör. ataç rozetinin sesli
 * karşılığı `{{count}}`). Adlı ve adsız anahtarın <b>ikisine de</b> geçer — ad boş olsa da sayı
 * kaybolmasın.
 */
export function rowActionLabel(
  t: TFunction,
  baseKey: string,
  name: string | null | undefined,
  params?: Record<string, string | number>,
): string {
  // `trim()` şart: yalnız boşluktan oluşan bir ad (kullanıcı taslak satıra boşluk yazdığında olur)
  // "dolu" sayılsaydı adlı anahtara gider ve " alt görevini sil" gibi baştan boşluklu, hâlâ ayırt
  // etmeyen bir etiket üretirdi — düzeltmenin amacını boşa çıkarırdı.
  if (!name?.trim()) return params ? t(baseKey, params) : t(baseKey)
  return t(`${baseKey}${NAMED_SUFFIX}`, { ...params, name })
}

/**
 * Silme düğmeleri için {@link rowActionLabel}'in adlandırılmış kısayolu. Davranışın tamamı orada
 * anlatılıyor; bu ad 39 çağrı yerinde kullanıldığı için korunuyor (salt-ad değişikliği teslim
 * diff'ini gereksiz büyütürdü).
 */
export function deleteLabel(t: TFunction, baseKey: string, name: string | null | undefined): string {
  return rowActionLabel(t, baseKey, name)
}
